import {DateTime} from 'luxon';
import React, {FC} from 'react';

import {
  build12Or24hOptionsForFormat,
  TimeFormatsEnum,
} from '../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {filterValidDateTime} from '../../../libs/shared/core/src/helpers/datetime/luxonHelpers';
import {DateTimeFormatterBase} from '../../../libs/shared/ui/src/components/datetime/dateTimeFormatterBase';
import {ArbitraryCalendarEventModel} from '../../../src/models/api/arbitraryCalendarEventModel';
import {CalendarEventVersionModel} from '../../../src/models/api/calendarEventVersionModel';
import {
  calendarEventTimeFormat,
  computeCalendarStartEndDateTime,
} from '../../helpers/models/calendarEventVersionHelpers';

/*
 * Props.
 */

interface CalendarEventTimeFormatterProps {
  calendarEventVersion: CalendarEventVersionModel | ArbitraryCalendarEventModel;
  timeFormat: TimeFormatsEnum;
  instanceStartDate?: number;
  shouldUseMultiline?: boolean;
}

/*
 * Component.
 */

// Four cases handled:
// 1. All day event (1 day): "Tue Jul 18, 2018"
// 2. All day event (>1 day): "Tue Dec 2, 2018 - Wed Dec 3, 2018"'
// 3. Start and end on same day: "Tue Jul 18, 2017 10:30am - 11:30am PST". If the preferMutiline option is set,
//    we display this as Tue Jul 18, 2017 \n 10:30am - 11:30am PST".
// 4. Start and end on different days: "Tue Jul 18, 2017 10:30am - Tue Jul 20, 2017 12:00pm PST"
// We display dates in the local timezone of the user.
export const CalendarEventTimeFormatter: FC<CalendarEventTimeFormatterProps> = (props) => {
  const {calendarEventVersion, instanceStartDate, timeFormat, shouldUseMultiline} = props;
  const {allDayDate, sameDayTimeBase} = calendarEventTimeFormat;
  const instanceStartDateTime = instanceStartDate ? DateTime.fromMillis(instanceStartDate) : undefined;

  const {startDateTime, endDateTime} = computeCalendarStartEndDateTime(
    calendarEventVersion,
    filterValidDateTime(instanceStartDateTime),
  );

  // 1. All day event (1 day): "Tue Jul 18, 2018".
  const days = endDateTime.diff(startDateTime, 'days');
  if (calendarEventVersion.isAllDay && days.get('days') <= 1) {
    return <DateTimeFormatterBase value={startDateTime} format={allDayDate} />;
  }

  // 2. All day event (>1 day): "Tue Dec 2, 2018 - Wed Dec 3, 2018".
  if (calendarEventVersion.isAllDay) {
    return (
      <>
        <DateTimeFormatterBase value={startDateTime} format={allDayDate} /> &mdash;{' '}
        <DateTimeFormatterBase value={endDateTime.minus({days: 1})} format={allDayDate} />
      </>
    );
  }

  const {sameDayDateAndTimeBase, timeWithoutTimezoneBase, dateAndTimeBase} = calendarEventTimeFormat;
  const sameDayTimeFormat = decorateFormat(sameDayTimeBase, timeFormat);
  const sameDayDateAndTimeFormat = decorateFormat(sameDayDateAndTimeBase, timeFormat);
  const timeWithoutTimezoneFormat = decorateFormat(timeWithoutTimezoneBase, timeFormat);
  const dateAndTimeFormat = decorateFormat(dateAndTimeBase, timeFormat);

  // Use abbreviated format if this event spans a single day.
  const singleDayEvent = startDateTime.hasSame(endDateTime, 'day');
  const endDateTimeFormat = singleDayEvent ? sameDayTimeFormat : sameDayDateAndTimeFormat;

  // 3. Start and end on same day: "Tue Jul 18, 2017 10:30am - 11:30am PST". If the preferMutiline option is set,
  //    we display this as Tue Jul 18, 2017 \n 10:30am - 11:30am PST".
  if (singleDayEvent && shouldUseMultiline) {
    return (
      <>
        <DateTimeFormatterBase value={startDateTime} format={allDayDate} />
        {'\n'}
        <DateTimeFormatterBase value={startDateTime} format={timeWithoutTimezoneFormat} /> &mdash;{' '}
        <DateTimeFormatterBase value={endDateTime} format={sameDayTimeFormat} />
      </>
    );
  }

  // 4. Start and end on different days: "Tue Jul 18, 2017 10:30am - Tue Jul 20, 2017 12:00pm PST"
  //    We display dates in the local timezone of the user.
  return (
    <>
      <DateTimeFormatterBase value={startDateTime} format={dateAndTimeFormat} /> &mdash;{' '}
      <DateTimeFormatterBase value={endDateTime} format={endDateTimeFormat} />
    </>
  );
};

function decorateFormat(formatOptions: Intl.DateTimeFormatOptions, timeFormat: TimeFormatsEnum) {
  return {
    ...formatOptions,
    ...build12Or24hOptionsForFormat(timeFormat),
  };
}
